import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  iconSize,
  Stack,
  Typography,
  useSnackbar,
  fontStacks,
} from '@munifocus/ui';
import type { FormParagraphDto } from '@munifocus/contracts-gov';
import { apiErrorMessage } from '../lib/mutation-toast';
import { useDraftRuns, useSaveDraftRuns } from '../lib/authoring-api';
import { insertAtSelection } from '@munifocus/contracts-gov';
import { SnippetPicker } from './SnippetPicker';

/**
 * Edit a draft document without leaving the portal.
 *
 * The unit is the OOXML **run** — the smallest span of text carrying uniform
 * formatting — and not the paragraph, and not HTML. Writing edits back into the
 * runs of the original package leaves every tab stop, table, header, footer and
 * style exactly as it was; converting to HTML and back was measured against all
 * nine templates and changes page counts, drops every header and footer, and
 * destroys ~300 tab stops. The layout a lawyer approved does not survive that.
 *
 * The visible consequence is that a sentence Word split across several runs
 * shows here as several adjacent boxes. That is deliberate and is the price of
 * the guarantee: merging them into one editable paragraph means writing the
 * result back into a single run, which moves text relative to the tab stops
 * around it — measured at up to 12.6% of pixels changed. The boxes are laid out
 * inline so a paragraph still reads as a sentence.
 *
 * For anything this cannot express — inserting a paragraph, changing a table,
 * restyling — the download-and-edit-in-Word path is still there and is still the
 * right tool. This is for changing what the document *says*.
 */
export function DraftEditor({
  fileId,
  formId,
  name,
  open,
  onClose,
  language,
}: {
  fileId: string;
  formId: string;
  name: string;
  open: boolean;
  onClose: () => void;
  /** The letter's language, so an inserted snippet defaults to the right variant. */
  language?: 'en' | 'fr' | null;
}) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const content = useDraftRuns(fileId, formId, open);
  const save = useSaveDraftRuns(fileId);

  /** Edited text by run index. Sparse: untouched runs are never sent. */
  const [edits, setEdits] = useState<Record<number, string>>({});

  // A reopened dialog must not carry the previous session's unsaved edits into
  // a draft that may have been recreated under it.
  useEffect(() => {
    if (open) setEdits({});
  }, [open, formId]);

  const paragraphs = content.data?.paragraphs ?? [];
  const dirty = Object.keys(edits).length > 0;

  const textOf = (index: number, original: string) => edits[index] ?? original;

  /**
   * The run the officer was last in, and the caret inside it — a snippet chosen
   * from the picker (which takes the focus) is spliced in there.
   */
  const caret = useRef<{ index: number; start: number; end: number } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const trackCaret = (index: number, el: HTMLElement) => {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    let start = el.textContent?.length ?? 0;
    let end = start;
    if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
      const a = sel.anchorOffset;
      const b = sel.focusOffset;
      start = Math.min(a, b);
      end = Math.max(a, b);
    }
    caret.current = { index, start, end };
  };

  const insertSnippet = (text: string) => {
    const at = caret.current;
    if (!at) {
      snackbar.info(t('gov.snippets.pickRunFirst'));
      return;
    }
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-run-index="${at.index}"]`);
    const original = paragraphs.flatMap((p) => p.runs).find((r) => r.index === at.index)?.text;
    if (!el || original === undefined) return;
    const result = insertAtSelection(el.textContent ?? '', text, at.start, at.end);
    // The DOM owns the text while editing (see `body`), so write it there and
    // record the edit the way a blur would.
    el.textContent = result.text;
    caret.current = { index: at.index, start: result.caret, end: result.caret };
    onEdit(at.index, original, result.text);
  };

  const onEdit = (index: number, original: string, next: string) => {
    setEdits((current) => {
      const draft = { ...current };
      // Typing a value back to what it was un-dirties that run rather than
      // sending an identical write.
      if (next === original) delete draft[index];
      else draft[index] = next;
      return draft;
    });
  };

  const onSave = () => {
    if (!dirty) return onClose();
    save.mutate(
      {
        formId,
        runs: Object.fromEntries(Object.entries(edits)),
        // Which copy these edits were made against: if a colleague saved in
        // between, the API refuses rather than overwriting their work.
        expectedUpdatedAt: content.data?.updatedAt,
      },
      {
        onSuccess: () => {
          snackbar.success(t('gov.authoring.saved'));
          setEdits({});
          onClose();
        },
        onError: (error) => snackbar.error(apiErrorMessage(error)),
      },
    );
  };

  const body = useMemo(
    () =>
      paragraphs.map((paragraph: FormParagraphDto, position: number) => {
        // An empty paragraph is a spacer in the document; showing it as a gap
        // keeps the editor's shape recognisable against the real page.
        if (paragraph.runs.length === 0) return <Box key={position} sx={{ height: 12 }} />;
        return (
          <Box
            key={position}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: '2px',
              py: 0.25,
            }}
          >
            {paragraph.runs.map((run) => (
              <Box
                key={run.index}
                component="span"
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                data-run-index={run.index}
                aria-label={t('gov.authoring.runLabel', { n: run.index + 1 })}
                onKeyUp={(event: React.KeyboardEvent<HTMLSpanElement>) =>
                  trackCaret(run.index, event.currentTarget)
                }
                onMouseUp={(event: React.MouseEvent<HTMLSpanElement>) =>
                  trackCaret(run.index, event.currentTarget)
                }
                onFocus={(event: React.FocusEvent<HTMLSpanElement>) =>
                  trackCaret(run.index, event.currentTarget)
                }
                onBlur={(event: React.FocusEvent<HTMLSpanElement>) =>
                  onEdit(run.index, run.text, event.currentTarget.textContent ?? '')
                }
                sx={(theme) => ({
                  minWidth: 8,
                  px: '2px',
                  borderRadius: '2px',
                  whiteSpace: 'pre-wrap',
                  outline: 'none',
                  backgroundColor:
                    edits[run.index] !== undefined ? theme.palette.action.selected : 'transparent',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                  '&:focus': {
                    backgroundColor: theme.palette.action.hover,
                    boxShadow: `inset 0 -1px 0 ${theme.palette.primary.main}`,
                  },
                })}
              >
                {textOf(run.index, run.text)}
              </Box>
            ))}
          </Box>
        );
      }),
    // `edits` is deliberately NOT a dependency: re-rendering a contentEditable
    // on every keystroke moves the caret back to the start of the box. The DOM
    // holds the text while the officer types, and `onBlur` is what puts it into
    // state — so the only things this needs to rebuild for are a new document
    // and a language change.
    [paragraphs, t],
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('gov.authoring.title', { name })}</DialogTitle>
      <DialogContent dividers>
        {content.isLoading && <CircularProgress size={iconSize.lg} />}
        {content.isError && <Alert severity="error">{t('gov.authoring.loadError')}</Alert>}

        {content.data && (
          <Stack spacing={1.5}>
            <Alert severity="info">{t('gov.authoring.hint')}</Alert>
            <Box>
              <SnippetPicker context="letter" defaultLanguage={language} onInsert={insertSnippet} />
            </Box>
            <Box
              ref={bodyRef}
              sx={(theme) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 2,
                backgroundColor: theme.palette.background.paper,
                fontFamily: fontStacks.body,
                fontSize: '0.9rem',
                lineHeight: 1.6,
                maxHeight: '55vh',
                overflowY: 'auto',
              })}
            >
              {body}
            </Box>
            <Typography variant="caption">{t('gov.authoring.layoutNote')}</Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          {t('common.actions.cancel')}
        </Button>
        <Button variant="contained" onClick={onSave} disabled={save.isPending || !content.data}>
          {save.isPending
            ? t('gov.authoring.saving')
            : t('gov.authoring.save', { count: Object.keys(edits).length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
